import * as React from 'react';
import { useParams } from 'react-router-dom';
import { SideBarComponent } from './side-bar/sidebar.component';
import styles from './risk-profile.module.css';
import { LeadApi } from '../../apis/lead.api';

import { InvestmentReturnComponent } from '@assetmark/risk-components/dist/components/investmentReturn/investmentReturn';
import { RiskQuestionsComponent } from '@assetmark/risk-components/dist/components/riskQuestions/riskQuestion';
import RiskResultComponent from '@assetmark/risk-components/dist/riskResult/riskResult.component';
import { RemoteData } from '@assetmark/ui-kit/dist/utils/remote-data.utils';
import { TRiskProfile, TRiskProfileResponse } from '../../models/risk-profile.model';
import { useLead } from '../../contexts/lead/lead.context';
import { useRisk } from '../../contexts/risk/risk.context';
import { RiskResultOverlay } from './risk-overlay/risk-result-overlay';

import {
	UPDATE_INVEST_RETURN_DATA,
	UPDATE_MAX_DECLINE_DATA,
	UPDATE_STOCK_MARKET_DATA,
	UPDATE_AGE_RANGE_DATA,
	UPDATE_PLANNED_DATA,
	UPDATE_SIDEBAR_TEXT_DATA,
} from '../../contexts/risk/risk.constant';
import { TRiskQuestion } from '../../models/risk-question.model';
import { TQuestionData } from '@assetmark/risk-components/dist/models/riskQuestion.model';
import { LeadQuestionAnswers } from '../../lib/das-api/model/leadQuestionAnswers';
import { getInvestmentReturnModels, getSelectedAgeId } from '../../helpers/risk.helper';
import { useState } from 'react';
import { TLeadRiskResponse } from '../../models/lead-risk-response';

type TRiskProfileProps = {
	riskTypeProfile: RemoteData<Array<TRiskProfile>>;
	riskProfileData: RemoteData<Array<TRiskProfileResponse>>;
	loadAllQusDataData: (leadId: string, QusId: number, AgId: string) => void;
	riskQuestions: TRiskQuestion[];
	onSaveRiskQuestion: (
		leadId: string,
		body: Array<LeadQuestionAnswers>,
		agentId: string,
		currentRoute: string,
	) => void;
	leadRisk: TLeadRiskResponse;
};

export const RiskProfileComponent: React.FC<TRiskProfileProps> = ({
	riskProfileData,
	loadAllQusDataData,
	riskQuestions,
	onSaveRiskQuestion,
	leadRisk,
}) => {
	const { agentId, leadProfileId, leadProfile } = useLead();
	const { assessData, updateRiskData, updateInvestmentRangeData } = useRisk();
	const { subModule } = useParams<{ subModule: string }>();

	const [riskProfileComplete, setRiskProfileComplete] = useState(true);

	React.useEffect(() => {
		// if(investReturnData && investReturnData.choices && investReturnData.choices.length === 0) {
		loadAllQusDataData(leadProfileId, 6, agentId);
		// }
	}, []);

	React.useEffect(() => {
		if (riskQuestions.length) {
			updateRiskData(riskQuestions);
		}
	}, [riskQuestions]);

	React.useEffect(() => {
		if (riskProfileData.isSuccess()) {
			updateInvestmentRangeData(riskProfileData.value);
		}
	}, [riskProfileData]);

	const onInvestmentReturnBackButtonClick = (): void => {
		if (leadProfile.investmentReason === '3') {
			LeadApi.redirectToPage$.next('goals/investment_target');
		} else if (leadProfile.investmentReason === '2') {
			LeadApi.redirectToPage$.next('goals/annual_contribution');
		} else {
			LeadApi.redirectToPage$.next('investment_reason');
		}
	};

	const onBackButtonClick = (screen: string): void => {
		LeadApi.redirectToPage$.next(screen);
	};

	const onNextButtonClick = (data: TQuestionData, currentRoute: string) => {
		const { investmentInfo, maximumDecline, stockMarketForecast, ageRange, plannedWithdrawal } = assessData;
		const selectedChoices = [
			{ questionId: investmentInfo.questionId, answerId: investmentInfo.selectedChoiceId },
			{ questionId: maximumDecline.questionId, answerId: maximumDecline.selectedChoiceId },
			{ questionId: stockMarketForecast.questionId, answerId: stockMarketForecast.selectedChoiceId },
			{ questionId: ageRange.questionId, answerId: ageRange.selectedChoiceId },
			{ questionId: plannedWithdrawal.questionId, answerId: plannedWithdrawal.selectedChoiceId },
		];

		const dataTobeUpdated: LeadQuestionAnswers[] = [];
		selectedChoices.forEach(ch => {
			if (ch.questionId === data.questionId) {
				dataTobeUpdated.push({ ...ch, answerId: data.selectedChoiceId });
			} else {
				dataTobeUpdated.push(ch);
			}
		});

		onSaveRiskQuestion(leadProfileId, dataTobeUpdated, agentId, currentRoute);
	};

	const onRestart = () => {
		onBackButtonClick('risk-profile/investment_return');
	};

	const onReview = () => {
		onBackButtonClick('your-analysis');
	};

	// const resultData = {
	//     profileId: 1,
	//     riskProfileDescription:
	//       'You seek to balance risks of capital loss with capital appreciation.',
	//     isVisible: true,
	//     investmentAmount: 10000,
	//     model: {
	//       AverageReturn: 10650,
	//       AverageReturnPercent: 6.5,
	//       Gain: 11570,
	//       GainPercent: 15.7,
	//       Key: 1,
	//       Loss: 9630,
	//       LossPercent: -3.7,
	//     },
	//     isRatingVisible: true,
	//     isAllocationVisible: true,
	//     isReturnVisible: true,
	//     showPdfDownload: true
	//   };

	return (
		<div className={styles.riskContainer}>
			<div className={styles.colFlex}>
				<div className={styles.sidebar}>
					{assessData.invesetmentRangeData.length && <SideBarComponent subModule={subModule} />}
				</div>
				<div className={`w-full ${styles.flexBox}`}>
					{subModule === 'investment_return' &&
						assessData.investmentInfo &&
						assessData.investmentInfo.choices &&
						assessData.investmentInfo.choices.length > 0 &&
						assessData.invesetmentRangeData.length && (
							<InvestmentReturnComponent
								invesetmentRangeData={assessData.invesetmentRangeData}
								data={assessData.investmentInfo}
								investmentAmount={
									leadProfile && leadProfile.investmentInfo
										? leadProfile.investmentInfo?.InvestmentAmount
										: 0
								}
								onBackButtonClick={() => onInvestmentReturnBackButtonClick()}
								onNextButtonClick={(e, data) => onNextButtonClick(data, 'investment_return')}
							/>
						)}
					{subModule === 'maximum_decline' && assessData.maximumDecline && (
						<RiskQuestionsComponent
							investmentAmount={leadProfile.investmentInfo?.InvestmentAmount}
							backButtonClassName="button button-secondary button-lg"
							nextButtonClassName={'button button-primary button-lg ml-2'}
							data={assessData.maximumDecline}
							onBackButtonClick={() => onBackButtonClick('risk-profile/investment_return')}
							onNextButtonClick={(e, data) => onNextButtonClick(data, 'maximum_decline')}
						/>
					)}
					{subModule === 'stock_market' && assessData.stockMarketForecast && (
						<RiskQuestionsComponent
							investmentAmount={
								leadProfile && leadProfile.investmentInfo
									? leadProfile.investmentInfo?.InvestmentAmount
									: 0
							}
							backButtonClassName="button button-secondary button-lg"
							nextButtonClassName={'button button-primary button-lg ml-2'}
							data={assessData.stockMarketForecast}
							onBackButtonClick={() => onBackButtonClick('risk-profile/maximum_decline')}
							onNextButtonClick={(e, data) => onNextButtonClick(data, 'stock_market')}
						/>
					)}
					{subModule === 'age_range' && assessData.ageRange && (
						<RiskQuestionsComponent
							investmentAmount={
								leadProfile && leadProfile.investmentInfo
									? leadProfile.investmentInfo?.InvestmentAmount
									: 0
							}
							backButtonClassName="button button-secondary button-lg"
							nextButtonClassName={'button button-primary button-lg ml-2'}
							data={
								assessData.ageRange.selectedChoiceId
									? assessData.ageRange
									: {
											...assessData.ageRange,
											selectedChoiceId: getSelectedAgeId(
												assessData.ageRange,
												leadProfile.Investor.Age || 0,
											),
									  }
							}
							onBackButtonClick={() => onBackButtonClick('risk-profile/stock_market')}
							onNextButtonClick={(e, data) => onNextButtonClick(data, 'age_range')}
						/>
					)}
					{(subModule === 'planned_withdrawal' || subModule === 'risk_result') &&
						assessData.plannedWithdrawal && (
							<>
								<RiskQuestionsComponent
									investmentAmount={
										leadProfile && leadProfile.investmentInfo
											? leadProfile.investmentInfo?.InvestmentAmount
											: 0
									}
									backButtonClassName="button button-secondary button-lg"
									nextButtonClassName={'button button-primary button-lg ml-2'}
									data={assessData.plannedWithdrawal}
									onBackButtonClick={() => onBackButtonClick('risk-profile/age_range')}
									onNextButtonClick={(e, data) => onNextButtonClick(data, 'planned_withdrawal')}
								/>
								{subModule === 'risk_result' && (
									<RiskResultOverlay
										profileId={leadRisk.riskProfileId}
										riskProfiles={
											riskProfileData.isSuccess()
												? riskProfileData.value
												: ([] as TRiskProfileResponse[])
										}
										investmentAmount={leadRisk.investmentAmount}
										model={getInvestmentReturnModels(
											assessData.invesetmentRangeData,
											leadRisk.investmentAmount,
											leadRisk.riskProfileId,
										)}
										onRestart={onRestart}
										onReview={onReview}
									/>
								)}
							</>
						)}
				</div>
			</div>
		</div>
	);
};
